import { fipavLogoProxyUrl } from '@/lib/logo/proxy-url'
import type { ScorebugThemeConfig } from '@/lib/live/state'

interface Props {
  name: string
  logo?: string
  setsWon: number
  timeoutsUsed: number
  maxTimeouts?: number
  side: 'home' | 'away'
  hasService: boolean
  setsDisplay?: 'dots' | 'number'
  className?: string
  theme?: ScorebugThemeConfig
}

export default function TeamBlock({
  name, logo, setsWon, timeoutsUsed, maxTimeouts = 2,
  side, hasService, setsDisplay = 'dots', className = '', theme,
}: Props) {
  const isRight = side === 'away'
  // Colori propri di questa squadra: se non impostati valgono quelli generali.
  const own = side === 'home'
    ? { text: theme?.homeText, logoBg: theme?.homeLogoBg, serviceColor: theme?.homeServiceColor }
    : { text: theme?.awayText, logoBg: theme?.awayLogoBg, serviceColor: theme?.awayServiceColor }
  const testo = own.text ?? theme?.text ?? 'var(--sb-text, #ffffff)'
  const sfondoLogo = own.logoBg ?? theme?.logoBg ?? 'var(--sb-logo-bg, rgba(255,255,255,0.1))'
  const battuta = own.serviceColor ?? theme?.serviceColor ?? 'var(--sb-service-color, #FE7200)'

  return (
    <div data-section={side} className={`flex flex-col ${isRight ? 'items-end' : 'items-start'} gap-1 ${className}`}>
      {/* Logo + Nome */}
      <div className={`flex items-center gap-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
        {logo ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={fipavLogoProxyUrl(logo)!}
            alt={name}
            className="w-16 h-16 object-contain rounded-full"
            style={{ background: sfondoLogo }}
          />
        ) : (
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: sfondoLogo }}>
            <span className="font-display font-bold uppercase" style={{ fontSize: 24, color: 'var(--sb-text-muted, rgba(255,255,255,0.4))' }}>
              {name.charAt(0)}
            </span>
          </div>
        )}
        {/* «Tabellone TV»: il nome è uno dei due dati che si devono leggere a
            distanza. Prima era `text-sm` (14 px) e per giunta `truncate
            max-w-[120px]`: su una grafica 1920 finiva tagliato a ~8 caratteri.
            40 px è il floor già usato dal segnapunti compatto. */}
        <span className="font-display font-bold uppercase" style={{ fontSize: 40, lineHeight: 1.05, color: testo }}>
          {name}
        </span>
      </div>

      {/* Set vinti */}
      {setsDisplay === 'number' ? (
        <div className={`flex items-center gap-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
          <span className="font-display font-bold uppercase tracking-[0.2em]" style={{ fontSize: 18, color: 'var(--sb-text-muted, rgba(255,255,255,0.45))' }}>Set</span>
          <span className="font-display font-bold tabular-nums leading-none" style={{ fontSize: 32, color: testo }}>{setsWon}</span>
        </div>
      ) : (
        <div className={`flex gap-1 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
          {Array.from({ length: 3 }, (_, i) => (
            <span
              key={i}
              className="w-6 h-6 rounded-full border"
              style={{ borderColor: 'var(--sb-chip-border, rgba(255,255,255,0.3))', backgroundColor: i < setsWon ? 'var(--sb-sets-bg, #ffffff)' : 'transparent' }}
            />
          ))}
        </div>
      )}

      {/* Timeout */}
      <div className={`flex gap-1 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
        {Array.from({ length: maxTimeouts }, (_, i) => (
          <span
            key={i}
            className="w-[18px] h-[18px] rounded-full border"
            style={{ borderColor: 'var(--sb-chip-border, rgba(255,255,255,0.2))', backgroundColor: i < timeoutsUsed ? battuta : 'transparent' }}
            title={i < timeoutsUsed ? 'Timeout usato' : 'Timeout disponibile'}
          />
        ))}
      </div>

      {/* Indicatore servizio */}
      {hasService && (
        <span className="font-bold animate-pulse" style={{ fontSize: 24, color: battuta }}>▶</span>
      )}
    </div>
  )
}
